import { FileText, Plus, RefreshCw } from "lucide-react";
import type { ReportGroup } from "../../../hooks";

interface ReportGroupSectionProps {
  reportGroups: readonly ReportGroup[];
  loading: boolean;
  working: boolean;
  onAdd: () => void;
  onEdit: (reportGroup: ReportGroup) => void;
  onGenerate: (reportGroup: ReportGroup) => void;
}

/** 設問構成に並んでいる設問の数。レポートに何問載るかの目安として出す */
function countQuestions(reportSettingJson: Record<string, unknown>): number {
  const sections = reportSettingJson.sections;
  if (!Array.isArray(sections)) return 0;
  return sections.reduce((total: number, section) => {
    const questions = (section as { questions?: unknown }).questions;
    return total + (Array.isArray(questions) ? questions.length : 0);
  }, 0);
}

export function ReportGroupSection({
  reportGroups,
  loading,
  working,
  onAdd,
  onEdit,
  onGenerate,
}: ReportGroupSectionProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <div>
          <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
            レポート
          </h2>
          <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
            取り込んだ回答をまとめて、受け取る人ごとのレポートを作ります
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="flex cursor-pointer items-center gap-1.5 rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2 font-medium text-sm text-white hover:bg-[#045a94]"
        >
          <Plus className="h-4 w-4" />
          レポート設定を追加
        </button>
      </div>
      {loading ? (
        <div className="p-12 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      ) : reportGroups.length === 0 ? (
        <div className="p-12 text-center">
          <FileText className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
          <p className="text-[var(--color-text-secondary)]">
            レポート設定がまだありません。回答の取り込みが済んでから追加してください
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--color-bg-subtle-gray)]">
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  レポート名
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  設問数
                </th>
                <th className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {reportGroups.map((reportGroup) => (
                <tr
                  key={reportGroup.reportGroupId}
                  className="border-[var(--color-border-default)] border-b"
                >
                  <td className="px-4 py-3 text-sm">{reportGroup.name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">
                    {countQuestions(reportGroup.reportSettingJson)}問
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(reportGroup)}
                        className="cursor-pointer rounded-md border border-[var(--color-border-default)] bg-white px-3 py-1.5 font-medium text-[13px] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle-gray)]"
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        onClick={() => onGenerate(reportGroup)}
                        disabled={working}
                        className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 font-medium text-[13px] ${
                          working
                            ? "cursor-not-allowed border-[var(--color-border-default)] text-[#9ca3af]"
                            : "cursor-pointer border-[var(--color-primary-500)] text-[var(--color-primary-500)] hover:bg-[var(--color-bg-subtle-gray)]"
                        }`}
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        レポートを書き出す
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
